import { Close } from '@mui/icons-material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box } from '@mui/system';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromDBToLocalDateTime, getDateTime } from '../../helpers/date';
import { estimatedTimeCompletion } from '../../helpers/orders';
import GenButton from '../GenButton';
import { GenDate } from '../GenDataFormat';

export const WOrderModalEditCompletionTime = ({
  data,
  open,
  isLoading,
  onCancel,
  onChangeCompletionTime,
}) => {
  const { t } = useTranslation('workOrdersTable');
  const [promiseDate, setPromiseDate] = useState(null);
  const [returnToEstimated, setReturnToEstimated] = useState(false);
  const [invalidDate, setInvalidDate] = useState(false);

  const savedPromiseDate = useRef(null);

  useEffect(() => {
    if (promiseDate && !savedPromiseDate.current)
      savedPromiseDate.current = promiseDate;
  }, [promiseDate]);

  const estimatedCompletionTime = estimatedTimeCompletion(data)
    ?.toJSDate()
    ?.getTime();

  const isBeforeTime =
    new Date().getTime() <
    (new Date(data?.promiseDate).getTime() || estimatedCompletionTime);

  const isCurrentTimeBelowCompletionDate =
    new Date().getTime() > estimatedCompletionTime;

  const isCurrentTimeOverPromiseDate = !isBeforeTime;

  useEffect(() => {
    if (!open) return;
    if (isBeforeTime) {
      setPromiseDate(
        fromDBToLocalDateTime(data.promiseDate) || estimatedTimeCompletion(data)
      );
    } else {
      setPromiseDate(
        getDateTime(new Date(), { zone: 'utc' }).plus({
          minutes: estimatedTimeCompletion(data, 1, true),
        })
      );
    }
    setReturnToEstimated(false);
  }, [data, open, isBeforeTime]);

  const triggerSaveChanges = () => {
    onChangeCompletionTime({ returnToEstimated, promiseDate });
  };

  const actualPromiseDate = estimatedTimeCompletion(data);

  const currentPromiseDateOverEvent = e =>
    e.getTime() < new Date(savedPromiseDate.current).getTime();

  const estimatedCompletionOrCurrentTimeOverEvent = e =>
    e.getTime() < actualPromiseDate?.toJSDate().getTime() ||
    e.getTime() < new Date().getTime();

  const handleIsInvalidDate = e => {
    if (isCurrentTimeOverPromiseDate || isCurrentTimeBelowCompletionDate)
      return currentPromiseDateOverEvent(e);
    return estimatedCompletionOrCurrentTimeOverEvent(e);
  };

  const onChangePromiseDate = event => {
    if (!event.getTime) return;
    setPromiseDate(event);
    setInvalidDate(handleIsInvalidDate(event));
  };

  const handleClose = () => {
    onCancel();
    setInvalidDate(false);
  };
  return (
    <Dialog open={open} maxWidth="xs" fullWidth={true}>
      <DialogTitle sx={{ background: theme => theme.palette.grey[50] }}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography variant="subtitle2" flexGrow={1}>
            {t('detail.modalCompletion.title')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack pt={4} gap={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              disabled={returnToEstimated}
              value={promiseDate}
              onChange={onChangePromiseDate}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  data-testid="datepicker"
                  sx={{ background: theme => theme.palette.background.paper }}
                />
              )}
            />
            <TimePicker
              label="Time"
              disabled={returnToEstimated}
              value={promiseDate}
              onChange={onChangePromiseDate}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ background: theme => theme.palette.background.paper }}
                />
              )}
            />
          </LocalizationProvider>
          {isBeforeTime && !isCurrentTimeBelowCompletionDate && (
            <Box pl={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={returnToEstimated}
                    onChange={event => {
                      setInvalidDate(false);
                      setReturnToEstimated(event.target.checked);
                      setPromiseDate(estimatedTimeCompletion(data));
                    }}
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body1">
                      {t('detail.modalCompletion.scheduleSoonest')}
                    </Typography>
                    <Typography variant="caption">
                      <GenDate date={estimatedTimeCompletion(data)} />
                    </Typography>
                  </Stack>
                }
              />
            </Box>
          )}
          {invalidDate && (
            <Alert variant="filled" severity="error">
              {t('detail.modalCompletion.invalidDate')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <GenButton
          text={t('detail.modalCompletion.cancel')}
          onClick={handleClose}
          data-testid="closeButton"
          disabled={isLoading}
          sx={{
            color: theme => theme.palette.text.primary,
            borderColor: theme => theme.palette.divider,
          }}
        />
        <GenButton
          text={t('detail.modalCompletion.save')}
          variant="contained"
          loading={isLoading}
          disabled={invalidDate}
          onClick={triggerSaveChanges}
        />
      </DialogActions>
    </Dialog>
  );
};
